/* eslint-disable @typescript-eslint/no-unused-vars */
import { ComponentType, FC, useState } from 'react';
import { Box, useMultiStyleConfig } from '@chakra-ui/react';
import {
  CardCvcElementProps,
  CardExpiryElementProps,
  CardNumberElementProps,
  ElementProps,
} from '@stripe/react-stripe-js';
import {
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementOptions,
} from '@stripe/stripe-js';

import * as Form from 'components/_core/form';
import { FieldProps } from 'components/_core/form';
import useTheme from 'theme/hooks/useTheme';

type StripeElementProps =
  | CardNumberElementProps
  | CardExpiryElementProps
  | CardCvcElementProps;

interface StripeElementModifierProps
  extends Pick<FieldProps, 'label'>,
    Pick<StripeElementProps, 'onChange' | 'onBlur' | 'onFocus'> {
  disabled: boolean;
  stripeElement: ComponentType<
    ElementProps & {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange?: (event: any) => void;
      options?:
        | StripeCardNumberElementOptions
        | StripeCardExpiryElementOptions
        | StripeCardCvcElementOptions;
    }
  >;
  elementProps?: StripeElementProps;
}

/**
 * Wrap a Stripe's element in our InputWrapper
 */
const StripeElementModifier: FC<StripeElementModifierProps> = ({
  disabled,
  elementProps = {},
  label,
  onBlur,
  onChange,
  onFocus,
  stripeElement: StripeElement,
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const inputStyles = useMultiStyleConfig('Input', {});

  return (
    <Form.Field label={label}>
      <Box
        __css={inputStyles.field}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        {...(isFocused && {
          borderColor: 'input-borderColor-focus',
        })}
      >
        <StripeElement
          className="WrappedElement"
          {...elementProps}
          onBlur={(element) => {
            setIsFocused(false);
            if (onBlur) {
              onBlur(element);
            }
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(event: any) => {
            if (onChange) {
              onChange(event);
            }
          }}
          onFocus={(element) => {
            setIsFocused(true);
            if (onFocus) {
              onFocus(element);
            }
          }}
          options={{
            disabled,
            placeholder: '',
            style: {
              base: {
                color: theme.semanticTokens.colors['input-color'],
                fontSize: theme.fontSizes['font-15'],
              },
            },
            ...(elementProps.options ?? {}),
          }}
        />
      </Box>
    </Form.Field>
  );
};

export default StripeElementModifier;
